import {
  FileUpload,
  RemoveCircle,
  SettingsSuggest,
  ControlPointDuplicate,
  ContentCopy,
} from '@mui/icons-material'
import { Delete, Edit, MoreHoriz } from '@workwave-tidal/core/icons'

import { useUnassignOrderModal } from '@/components/modals/UnassignOrderModal'
import { useResidentModalsApi } from '@/components/modals/ResidentModals'
import { useFitInOrderModal } from '@/components/modals/FitInOrderModal'
import { preview } from '@/components/form/preview'

import { useTexts } from './useTexts'
import { useBulkViewActionsData } from './hooks/useBulkViewActionsData'
import { useExportOrderButtonData } from './hooks/useExportOrderButtonData'

type Props = {
  orders: uui.domain.client.rm.ExtendedOrderStep[]
}

export function PreviewActions(props: Props) {
  const { orders } = props
  const texts = useTexts()

  const { show: showUnassignOrderModal, Modal: UnassignOrderModal } = useUnassignOrderModal()

  const { show: showFitInOrderModal, Modal: FitInOrderModal } = useFitInOrderModal()

  const {
    deleteOrders: { show: handleDeleteOrders },
    duplicateOrders: { show: handleDuplicateOrders },
    copySelectedOrders: { show: handleCopySelectedOrders },
  } = useResidentModalsApi()

  const {
    isSimulation,
    allUnassigned,
    onStartEditing,
    canDuplicateOrders,
    assignedOrderStepIds,
    unassignedOrderStepIds,
    duplicateDisabledTooltipTitle,
    canUnassign,
    canUnassignTitle,
  } = useBulkViewActionsData(orders)

  const { exportOrders } = useExportOrderButtonData(orders, [
    ...assignedOrderStepIds,
    ...unassignedOrderStepIds,
  ])

  return (
    <>
      <preview.ActionBar
        menuIcon={<MoreHoriz />}
        menuMode="textOnly"
        menuTooltip={texts.more}
        testId="orders-view-actions"
      >
        <preview.ActionBarItem
          icon={<Edit />}
          label={texts.edit}
          tooltip={texts.edit}
          onClick={onStartEditing}
          data-testid="orders__bulk-edit-button"
          data-trackid="pendo-order__form-bulkedit"
          disableWhenReadOnly
        />

        {unassignedOrderStepIds.length > 0 ? (
          <preview.ActionBarItem
            icon={<SettingsSuggest />}
            onClick={showFitInOrderModal}
            testId="order__form-bulk-fitin"
            data-trackId="pendo-order__form-bulk-fitin"
            label={texts.fitIn(allUnassigned, unassignedOrderStepIds.length)}
            tooltip={texts.fitIn(allUnassigned, unassignedOrderStepIds.length)}
            disableWhenReadOnly
          />
        ) : (
          <></>
        )}

        {assignedOrderStepIds.length > 0 ? (
          <preview.ActionBarItem
            icon={<RemoveCircle />}
            disabled={!canUnassign}
            tooltip={
              !canUnassign
                ? canUnassignTitle
                : texts.unassign(allUnassigned, assignedOrderStepIds.length)
            }
            onClick={showUnassignOrderModal}
            testId="order__form-bulkunassign"
            data-trackId="pendo-order__form-bulkunassign"
            label={texts.unassign(allUnassigned, assignedOrderStepIds.length)}
            disableWhenReadOnly
          />
        ) : (
          <></>
        )}

        <preview.ActionBarItem
          key="delete"
          icon={<Delete />}
          label={texts.delete}
          tooltip={texts.delete}
          onClick={() => handleDeleteOrders([...assignedOrderStepIds, ...unassignedOrderStepIds])}
          data-testid="orders__delete-bulk-button"
          data-trackid="pendo-order__form-bulkdelete"
          disableWhenReadOnly
        />

        <preview.ActionBarItem
          key="duplicate"
          disabled={!canDuplicateOrders}
          tooltip={!canDuplicateOrders ? duplicateDisabledTooltipTitle : texts.duplicateAll}
          icon={<ControlPointDuplicate />}
          label={texts.duplicateAll}
          onClick={() =>
            handleDuplicateOrders([...assignedOrderStepIds, ...unassignedOrderStepIds])
          }
          data-testid="order__form-bulkduplicate"
          data-trackid="pendo-order__form-bulkduplicate"
          disableWhenReadOnly
          mode="menu"
        />

        <preview.ActionBarItem
          key="export"
          icon={<FileUpload />}
          label={texts.export}
          onClick={exportOrders}
          tooltip={texts.export}
          data-testid="order__form-bulkexport"
          data-trackid="pendo-order__form-bulkexport"
          disableWhenReadOnly={false}
          mode="menu"
          divider={isSimulation}
        />

        {isSimulation ? (
          <preview.ActionBarItem
            icon={<ContentCopy />}
            onClick={() =>
              handleCopySelectedOrders([...assignedOrderStepIds, ...unassignedOrderStepIds])
            }
            testId="orders-copy-to-operations"
            label={`${texts.copyAllToOperations}...`}
            disableWhenReadOnly
            mode="menu"
          />
        ) : (
          <></>
        )}
      </preview.ActionBar>

      <UnassignOrderModal orderStepIds={assignedOrderStepIds} />
      <FitInOrderModal orderStepIds={unassignedOrderStepIds} />
    </>
  )
}
